#!/usr/bin/env node
import type { Server } from 'node:http';
import type { WebSocketServer } from 'ws';
import type { Request, Response } from 'express';
import { fileURLToPath, URL as NodeURL } from 'node:url';
import { dirname, join, resolve } from 'node:path';
import { readFileSync } from 'node:fs';
import express from 'express';
import dotenv from 'dotenv'
import open from 'open';
import { resolvePort } from './port-resolution.js';

const __dirname = dirname(fileURLToPath(import.meta.url));

const { version } = JSON.parse(
  readFileSync(new NodeURL('./package.json', import.meta.url), 'utf8'),
) as { version: string };

function printStartupTable(port: number, appUrl: string): void {
  const rows: [string, string][] = [
    ['Version', version],
    ['Port', String(port)],
    ['Url', appUrl],
  ];

  const col0 = Math.max(...rows.map(([k]) => k.length));
  const col1 = Math.max(...rows.map(([, v]) => v.length));

  const pad = (s: string, n: number) => s.padEnd(n);
  const hr = (l: string, m: string, r: string, f: string) =>
    l + f.repeat(col0 + 2) + m + f.repeat(col1 + 2) + r;

  console.log('');
  console.log(hr('┌', '┬', '┐', '─'));
  for (const [key, value] of rows) {
    console.log(`│ ${pad(key, col0)} │ ${pad(value, col1)} │`);
  }
  console.log(hr('└', '┴', '┘', '─'));
  console.log('');
}

// Parse CLI flags
const args = process.argv.slice(2);

// Resolve target directory (first positional arg, skipping flag values)
const flagsWithValues = new Set(['--host', '--port', '-e', '--environment', '-s', '--service']);
const targetDir = args.find((a, i) => !a.startsWith('-') && !flagsWithValues.has(args[i - 1] ?? ''));
if (targetDir) {
  const resolved = resolve(targetDir);
  process.chdir(resolved);
  console.log(`[cli] Working directory: ${resolved}`);
} else if (process.env['DATA_DIR']) {
  const resolved = resolve(process.env['DATA_DIR'], '..');
  process.chdir(resolved);
  console.log(`[cli] Working directory: ${resolved} (from DATA_DIR)`);
}

dotenv.config({ quiet: true });

if (args.includes('--help') || args.includes('-h')) {
  console.log(`Usage: runeya [path-to-your-stack] [options]

Options:
  --version               Show version number
  --pe, --pull-env        Print env vars for a service to stdout (requires -e and -s)
  -e, --environment <n>   Environment name or id
  -s, --service <name>    Service name or id
  --lan                   Expose server on the local network (shortcut for --host 0.0.0.0)
  --host <address>        Host to bind to (default: 127.0.0.1)
  --no-open               Do not open browser on startup
  --agent                 Run as agent only (no server, no UI) — for remote machines
  -h, --help              Show this help message
`);
  process.exit(0);
}

if (args.includes('--version')) {
  console.log(version);
  process.exit(0);
}

const noOpen =
  args.includes('--no-open') ||
  !!process.env['VITEST'] ||
  !!process.env['CI'] ||
  process.env['NODE_ENV'] === 'test';

// --pull-env flag — detect early so we can silence stdout before any log
const isPullEnv = args.includes('--pull-env') || args.includes('--pe');

// In pull-env mode, redirect console.log/info to stderr so only KEY=VALUE goes to stdout
if (isPullEnv) {
  const toStderr = (...a: unknown[]) => process.stderr.write(a.join(' ') + '\n');
  console.log = toStderr;
  console.info = toStderr;
}

function getFlagValue(flags: string[]): string | undefined {
  for (const flag of flags) {
    const idx = args.indexOf(flag);
    if (idx !== -1 && idx + 1 < args.length) return args[idx + 1];
  }
  return undefined;
}

const serviceArg = getFlagValue(['-s', '--service']);
const environmentArg = getFlagValue(['-e', '--environment']);

// Configure environment BEFORE importing server (env is parsed at module load)
// RUNEYA_HTTP_PORT takes priority over PORT
if (process.env.RUNEYA_HTTP_PORT && !process.env.PORT) {
  process.env.PORT = process.env.RUNEYA_HTTP_PORT;
}
// AGENT_PORT is an alias for RUNEYA_AGENT_PORT (used by e2e test-server and legacy tooling)
if (process.env.AGENT_PORT && !process.env.RUNEYA_AGENT_PORT) {
  process.env.RUNEYA_AGENT_PORT = process.env.AGENT_PORT;
}
// --lan shortcut sets host to 0.0.0.0; --host flag takes priority over HOST env var
const isLan = args.includes('--lan');
const hostFlag = getFlagValue(['--host']);
const isLanProxy = isLan || process.env.RUNEYA_LAN_PROXY === 'true' || process.env.RUNEYA_LAN_PROXY === '1';
if (hostFlag) {
  process.env.HOST = hostFlag;
} else if (isLanProxy) {
  process.env.HOST = '0.0.0.0';
}
if (isLanProxy) {
  process.env.RUNEYA_LAN_PROXY = 'true';
}
// Agent binary path (embedded in CLI dist during build)
process.env.AGENT_BINARY_PATH = join(__dirname, 'agent/index.js');
// Expose CLI version to server
process.env.RUNEYA_VERSION = version;

// --agent mode: run as standalone agent only (no server, no UI)
const isAgentOnly = args.includes('--agent');
if (isAgentOnly) {
  const agentPort = getFlagValue(['--port']);
  const agentHost = getFlagValue(['--host']);
  if (agentPort) process.env.RUNEYA_AGENT_PORT = agentPort;
  // --host in agent mode binds the agent (default 127.0.0.1 — use --host 0.0.0.0 for remote access)
  // --lan also applies: sets agent host to 0.0.0.0 unless --host overrides it
  if (agentHost) {
    process.env.RUNEYA_AGENT_HOST = agentHost;
  } else if (isLanProxy) {
    process.env.RUNEYA_AGENT_HOST = '0.0.0.0';
  }
}
// Data directory defaults to CWD/.runeya (server env.ts default)
// so Runeya data lives alongside the target project.

/**
 * Listen with retry for EADDRINUSE — handles the race condition when the CLI
 * restarts the server before the old process has fully released the port.
 *
 * The `wss` parameter is needed because the `ws` library re-emits HTTP server
 * errors on the WebSocketServer — without a handler the error becomes unhandled.
 */
function listenWithRetry(
  server: Server,
  port: number,
  host: string,
  wss?: WebSocketServer,
  maxRetries = 20,
  delay = 500,
): Promise<void> {
  return new Promise((resolve, reject) => {
    let attempts = 0;

    // Absorb EADDRINUSE errors re-emitted by the ws library on the WSS
    // during the retry window. Without this, Node.js crashes on unhandled error.
    const wssErrorHandler = () => { /* absorbed — handled via server 'error' */ };
    wss?.on('error', wssErrorHandler);

    const tryListen = () => {
      const onError = (err: NodeJS.ErrnoException) => {
        // Clean up the 'listening' callback that server.listen() registered
        server.removeAllListeners('listening');

        if (err.code === 'EADDRINUSE' && attempts < maxRetries) {
          attempts++;
          console.log(`[cli] Port ${port} in use, retrying in ${delay}ms (${attempts}/${maxRetries})...`);
          setTimeout(tryListen, delay);
        } else {
          wss?.removeListener('error', wssErrorHandler);
          reject(err);
        }
      };

      server.once('error', onError);
      server.listen(port, host, () => {
        server.removeListener('error', onError);
        wss?.removeListener('error', wssErrorHandler);
        resolve();
      });
    };

    tryListen();
  });
}

async function main() {
  // Dynamic import ensures env is parsed AFTER we configure it
  const { createLocalServer, pullEnv, PullEnvError } = await import('@runeya/apps-server');

  // Handle --pull-env mode: resolve env vars and print, then exit
  if (isPullEnv) {
    if (!serviceArg) {
      console.error('Error: --service (-s) is required with --pull-env');
      process.exit(1);
    }
    if (!environmentArg) {
      console.error('Error: --environment (-e) is required with --pull-env');
      process.exit(1);
    }
    try {
      const output = await pullEnv({ service: serviceArg, environment: environmentArg });
      if (output) process.stdout.write(output + '\n');
      process.exit(0);
    } catch (err: unknown) {
      if (err instanceof PullEnvError) {
        console.error(`Error: ${err.message}`);
        process.exit(err.exitCode);
      }
      throw err;
    }
  }

  // --agent mode: standalone agent, no server, no UI
  if (isAgentOnly) {
    const { spawn } = await import('node:child_process');
    const agentBin = join(__dirname, 'agent/index.js');
    const child = spawn(process.execPath, [agentBin], { stdio: 'inherit' });
    child.on('exit', (code) => process.exit(code ?? 0));
    // SIGINT is already delivered to the child by the terminal (same process group).
    // Intercept it here only to suppress Node's default exit so we stay alive
    // until the child finishes its graceful shutdown.
    process.on('SIGINT', () => { /* wait for child to exit */ });
    process.on('SIGTERM', () => child.kill('SIGTERM'));
    return;
  }

  console.log('[cli] Starting Runeya...');

  // Create server (automatically spawns local agent)
  const { app, server, wss, shutdown } = await createLocalServer();

  // Serve static files from web frontend (embedded in CLI dist during build)
  const webDistPath = join(__dirname, 'web');
  app.use(express.static(webDistPath));

  // Fallback to index.html for client-side routing (Vue Router)
  // Express 5 requires explicit path patterns (not '*')
  app.use((_req: Request, res: Response) => {
    res.sendFile(join(webDistPath, 'index.html'));
  });

  // Start server — RUNEYA_HTTP_PORT takes priority over PORT
  const port = resolvePort();
  const host = process.env.HOST ?? '127.0.0.1';

  await listenWithRetry(server, port, host, wss);

  const appUrl = `http://${host}:${port}`;

  if (!noOpen) {
    await open(appUrl);
  }

  console.log('\n🚀  Runeya is running!');
  if (isLanProxy) {
    console.log('🌐  LAN mode: server and service ports exposed on local network');
  }
  printStartupTable(port, appUrl);
  console.log('[cli] Press Ctrl+C to stop');

  let shuttingDown = false;
  const gracefulShutdown = async (signal: string) => {
    if (shuttingDown) {
      console.log(`[cli] Forced exit (second ${signal})`);
      process.exit(1);
    }
    shuttingDown = true;
    console.log(`\n[cli] Received ${signal}, shutting down gracefully...`);

    // In dev, exit immediately — ports are freed on process death,
    // and the 'exit' handler in agent-spawner SIGKILLs the agent.
    if (process.env.NODE_ENV === 'development') {
      process.exit(0);
    }

    await shutdown();
    console.log('[cli] Shutdown complete');
    process.exit(0);
  };

  process.on('SIGTERM', () => gracefulShutdown('SIGTERM'));
  process.on('SIGINT', () => gracefulShutdown('SIGINT'));
}

main().catch((err) => {
  console.error('[cli] Fatal error:', err);
  process.exit(1);
});
